import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SignupPage from './pages/SignupPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import InvitationExpiredPage from './pages/InvitationExpiredPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import OnboardingWizardPage from './pages/OnboardingWizardPage';
import DashboardPage from './pages/DashboardPage';
import JobsListPage from './pages/JobsListPage';
import JobDetailPage from './pages/JobDetailPage';
import CreateJobPage from './pages/CreateJobPage';
import ChatJobPage from './pages/ChatJobPage';
import EditJobPage from './pages/EditJobPage';
import CandidateListPage from './pages/CandidateListPage';
import CandidateDetailPage from './pages/CandidateDetailPage';
import { InterviewPage } from './pages/InterviewPage';
import { InterviewResultPage } from './pages/InterviewResultPage';
import { UserManagementPage } from './pages/UserManagementPage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import AssistantPage from './pages/AssistantPage';
import LinkedInSettingsPage from './pages/LinkedInSettingsPage';
import LinkedInCallbackPage from './pages/LinkedInCallbackPage';
import ReportsDashboardPage from './pages/ReportsDashboardPage';
import JobReportPage from './pages/JobReportPage';
import RecruiterReportPage from './pages/RecruiterReportPage';
import PlatformReportPage from './pages/PlatformReportPage';
import CareerListPage from './pages/CareerListPage';
import CareerJobDetailPage from './pages/CareerJobDetailPage';
import CareerApplyPage from './pages/CareerApplyPage';
import CareerPageSettings from './pages/CareerPageSettings';
import { PublicLayout } from './layouts/PublicLayout';

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        } 
      />
      <Route 
        path="/signup" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupPage />
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />
        } 
      />
      <Route 
        path="/verify-email" 
        element={<EmailVerificationPage />} 
      />
      <Route 
        path="/invitation-expired" 
        element={<InvitationExpiredPage />} 
      />
      <Route 
        path="/accept-invitation/:token" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <AcceptInvitationPage />
        } 
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <JobsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/new"
        element={
          <ProtectedRoute>
            <CreateJobPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/create/chat"
        element={
          <ProtectedRoute>
            <ChatJobPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/create/chat/:sessionId"
        element={
          <ProtectedRoute>
            <ChatJobPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId/candidates"
        element={
          <ProtectedRoute>
            <CandidateListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId/candidates/:candidateId"
        element={
          <ProtectedRoute>
            <CandidateDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId/candidates/:candidateId/interview"
        element={
          <ProtectedRoute>
            <InterviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId/candidates/:candidateId/interviews/:interviewId"
        element={
          <ProtectedRoute>
            <InterviewResultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId/edit"
        element={
          <ProtectedRoute>
            <EditJobPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <OrganizationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistant"
        element={
          <ProtectedRoute>
            <AssistantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <LinkedInSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations/linkedin/callback"
        element={
          <ProtectedRoute>
            <LinkedInCallbackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/jobs/:jobId"
        element={
          <ProtectedRoute>
            <JobReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/recruiters"
        element={
          <ProtectedRoute>
            <RecruiterReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/platform"
        element={
          <ProtectedRoute>
            <PlatformReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/career-page"
        element={
          <ProtectedRoute>
            <CareerPageSettings />
          </ProtectedRoute>
        }
      />
      {/* Public career pages — no auth required */}
      <Route path="/careers/:orgSlug" element={<PublicLayout><CareerListPage /></PublicLayout>} />
      <Route path="/careers/:orgSlug/:jobId" element={<PublicLayout><CareerJobDetailPage /></PublicLayout>} />
      <Route path="/careers/:orgSlug/:jobId/apply" element={<PublicLayout><CareerApplyPage /></PublicLayout>} />
      <Route 
        path="/" 
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        } 
      />
    </Routes>
  );
}

export default App;
